<template>
  <div>
    <AlLoading
      size="lg"
      message="Retrieving Capacity Plans"
      v-if="pageLoading"
    />
    <div class="listing-page" v-if="!pageLoading">
      <div class="page-header">
        <PageTitle />

        <div class="page-header-actions">
          <AlButton
            v-on:click="createNewConfiguration()"
            v-if="!displayLandingPage"
            data-testid="go-to-create-a-plan"
          >
            <Localization message="capacity-planner-landing-button" />
          </AlButton>
        </div>
      </div>

      <div
        data-testid="no-data-message"
        class="landing-page-container"
        v-if="displayLandingPage"
      >
        <div class="image-wrapper">
          <img
            src="../assets/wfh-escenes-06.png"
            alt="Working from home on the couch"
          />
        </div>
        <p class="landing-page-title">
          <b>
            <Localization message="capacity-planner-landing-title" />
          </b>
        </p>
        <p class="landing-page-message">
          <Localization message="capacity-planner-landing-description" />
        </p>
        <router-link to="/create-configuration">
          <AlButton data-testid="go-to-create-a-plan" :is-secondary-gray="true">
            <Localization message="capacity-planner-landing-button" />
          </AlButton>
        </router-link>
      </div>

      <div
        data-testid="plan-listing"
        class="listing-page-container"
        v-if="!displayLandingPage"
      >
        <AlModal
          :config="deleteConfigurationModalConfig"
          :open-modal="deletePlanModalActive"
          @close="closeDeletePlanModal()"
        >
          <span>
            <Localization message="listing-delete-plan-modal-body" />
          </span>

          <div slot="footer" class="dr-buttons">
            <AlButton
              id="modal-button-cancel"
              :text="translate('listing-delete-plan-modal-cancel-button-text')"
              class="is-secondary-gray is-ghost"
              title="Cancel delete plan action"
              :disabled="isDeletingConfiguration"
              @click="closeDeletePlanModal()"
            ></AlButton>
            <AlButton
              id="modal-button-confirm-delete"
              :text="translate('listing-delete-plan-modal-delete-button-text')"
              class="is-primary-red"
              title="Confirm delete plan action"
              :disabled="isDeletingConfiguration"
              @click="confirmDeletePlan({ planToDelete: planStagedForDelete })"
            ></AlButton>
          </div>
        </AlModal>
        <PlanListingItem
          v-for="configuration in planConfigurations"
          :key="configuration._id"
          :item="configuration"
          @onListingItemClicked="openCapacityPlan({ configuration })"
          @onDeleteItem="openDeletePlanModal({ configuration })"
          @onEditItem="editCapacityPlan({ configuration })"
        />
      </div>

      <Toast
        v-model="showDeletedConfigurationToast"
        type="info"
        :duration="5000"
      >
        <template v-slot:title
          ><Localization message="listing-deleted-configuration-toast-title"
        /></template>
        <Localization
          message="listing-deleted-configuration-toast-message"
          :values="deletedConfigurationToastConfig"
        />
      </Toast>

      <Toast
        v-model="showDeletedConfigurationFailedToast"
        type="error"
        :duration="5000"
      >
        <template v-slot:title
          ><Localization
            message="listing-deleted-configuration-failed-toast-title"
        /></template>
        <Localization
          message="listing-deleted-configuration-failed-toast-message"
          :values="deletedConfigurationFailedToastConfig"
        />
      </Toast>
    </div>
  </div>
</template>

<script>
import { AlButton, AlModal, AlLoading } from "@playvox/almendra-components";
import PlanListingItem from "./PlanListingItem";
import { utcToZonedTime } from "date-fns-tz";

import {
  Localization,
  Translator,
  PageTitle,
  User,
  CapacityPlannerConfiguration,
  Toast,
} from "@agyletime/wfm-vue-common";

export default {
  name: "ListingPage",
  components: {
    Localization,
    AlButton,
    AlModal,
    PlanListingItem,
    PageTitle,
    Toast,
    AlLoading,
  },
  mixins: [Translator],
  created: async function () {
    try {
      this.pageLoading = true;

      let planConfigurations =
        await CapacityPlannerConfiguration.getConfigurations();

      const usersToGet = planConfigurations.reduce((acc, curr) => {
        if (curr.updatedByUserId) {
          acc.add(curr.updatedByUserId);
        }
        if (curr.createdByUserId) {
          acc.add(curr.createdByUserId);
        }
        return acc;
      }, new Set());

      this.allUsers = await User.getUsers({
        active: "all",
        userIds: Array.from(usersToGet),
      });

      this.planConfigurations = planConfigurations.map(
        this.enrichConfigurationUsers
      );
    } catch (err) {
      console.error(`Problem creating Listing Page: `, err);
    } finally {
      this.checkPlanConfigurationsAvailable();
      this.pageLoading = false;
    }
  },
  methods: {
    enrichConfigurationUsers(configuration) {
      if (configuration.updatedByUserId) {
        let updatedByUser = this.allUsers.find(
          (u) => u.id == configuration.updatedByUserId
        );
        if (updatedByUser) {
          configuration.updatedByUserName = `${updatedByUser.firstName} ${updatedByUser.lastName}`;
        }
      }

      if (configuration.createdByUserId) {
        let createdByUser = this.allUsers.find(
          (u) => u.id == configuration.createdByUserId
        );
        if (createdByUser) {
          configuration.createdByUserName = `${createdByUser.firstName} ${createdByUser.lastName}`;
        }
      }
      // covert UTC time into local time
      if (configuration.startDate && configuration.endDate) {
        this.convertUtcToLocalTime(configuration);
      }

      return configuration;
    },

    convertUtcToLocalTime(configuration) {
      configuration.startDate = utcToZonedTime(
        configuration.startDate,
        configuration.timezone
      );
      configuration.endDate = utcToZonedTime(
        configuration.endDate,
        configuration.timezone
      );
    },
    checkPlanConfigurationsAvailable() {
      // Check if any configurations exist, otherwise show landing page
      if (this.planConfigurations && this.planConfigurations.length) {
        this.displayLandingPage = false;
      } else {
        this.displayLandingPage = true;
      }
    },
    createNewConfiguration() {
      this.$router.push(`/create-configuration`);
    },
    openCapacityPlan({ configuration }) {
      // Navigate to view plan page
      this.$router.push({
        name: "details-view",
        params: { id: configuration._id },
      });
    },
    openDeletePlanModal({ configuration }) {
      if (configuration) {
        this.planStagedForDelete = configuration;
        this.deleteConfigurationModalConfig.header = this.translate(
          "listing-delete-plan-modal-heading",
          {
            planName: configuration.name,
          }
        );
        this.deletePlanModalActive = true;
      }
    },
    closeDeletePlanModal() {
      this.planStagedForDelete = undefined;
      this.deleteConfigurationModalConfig.header = this.translate(
        "listing-delete-plan-modal-heading-default"
      );
      this.deletePlanModalActive = false;
    },
    async confirmDeletePlan({ planToDelete }) {
      if (planToDelete) {
        try {
          this.isDeletingConfiguration = true;
          await planToDelete.delete();
          this.deletedConfigurationToastConfig = {
            planName: planToDelete.name,
          };
          this.removeDeletedPlanFromConfigurationsList({
            configurationId: planToDelete._id,
          });
          this.showDeletedConfigurationToast = true;
        } catch (e) {
          this.showDeletedConfigurationFailedToast = true;
          this.deletedConfigurationFailedToastConfig = {
            error: `${e}`,
          };
        } finally {
          this.isDeletingConfiguration = false;
          this.closeDeletePlanModal();
        }
      }
    },
    removeDeletedPlanFromConfigurationsList({ configurationId }) {
      if (
        configurationId &&
        this.planConfigurations &&
        this.planConfigurations.length
      ) {
        const indexToRemove = this.planConfigurations.findIndex(
          (pc) => pc._id === configurationId
        );
        this.planConfigurations.splice(indexToRemove, 1);
        this.checkPlanConfigurationsAvailable();
      }
    },
    editCapacityPlan({ configuration }) {
      const configurationId = configuration._id;
      this.$router.push(`/edit-configuration/${configurationId}`);
    },
  },
  data() {
    return {
      displayLandingPage: false,
      pageLoading: false,
      planConfigurations: [],
      deletePlanModalActive: false,
      deleteConfigurationModalConfig: {
        header: "Delete Plan",
      },
      isDeletingConfiguration: false,
      showDeletedConfigurationToast: false,
      deletedConfigurationToastConfig: {},
      showDeletedConfigurationFailedToast: false,
      deletedConfigurationFailedToastConfig: {},
    };
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.image-wrapper {
  max-width: 50vw;
  max-height: 50vh;
}

.listing-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.page-header {
  display: flex;
  flex-direction: row;
  align-self: flex-start;
  align-items: center;
  margin-top: 3rem;
  margin-bottom: 1.5rem;
  width: 100%;

  & .page-title {
    margin-bottom: 0;
  }
}

.page-header-actions {
  margin-left: auto;

  & button {
    margin-left: 1rem;
  }
}

// Landing
.landing-page-container {
  display: flex;
  flex-direction: column;
  align-items: center;

  button.dr-button.is-secondary-gray {
    color: var(--color-eerie-black-80);
    font-weight: normal;
  }
}

.landing-page-title {
  margin-bottom: 8px;
}

.landing-page-message {
  margin-bottom: 24px;
  max-width: 20rem;
  text-align: center;
}

// Listing
.listing-page-container {
  width: 100%;
}

@media screen and (min-device-width: 320px) and (max-device-width: 599px) {
  .listing-card {
    margin: 16px;
  }
}
@media screen and (min-device-width: 600px) and (max-device-width: 1023px) {
  .listing-card {
    margin: 48px;
  }
}
@media screen and (min-device-width: 1024px) and (max-device-width: 1200px) {
  .listing-card {
    margin: 64px;
  }
}
@media (min-device-width: 1200px) {
  .listing-card {
    margin: 80px;
  }
}
</style>
